import { decompress } from '@bokuweb/zstd-wasm';
import { logger } from '@fe/core';
import { mercatorUtils } from '@fe/maplibre-extensions';
import { clipPolygon } from 'lineclip';
import { getBbox, getBboxFromPoints, MS_TO_KT } from '../utils/geo';
import {
    GDFCcfpRecord,
    GDFCustomDrawCap,
    GDFCustomDrawEllipseType,
    GDFCustomDrawFillStyle,
    GDFCustomDrawPenStyle,
    GDFCustomDrawShape,
    GDFCustomRecord,
    GDFDateTime2Record,
    GDFDateTimeRecord,
    GDFDelayElement,
    GDFDelayRecord,
    GDFDiversionElement,
    GDFDiversionRecord,
    GDFFcaRecord,
    GDFFcaShape,
    GDFFrame,
    GDFFrameDefinitionRecord,
    GDFFrontRecord,
    GDFHeader,
    GDFHotPollyShape,
    GDFHotPollyTypes,
    GDFHotPolyHiRecord,
    GDFHotPolyHiRecordNormal,
    GDFHotPolyHiRecordReduced,
    GDFHotPolyRecord,
    GDFHotPolyRecordNormal,
    GDFHotPolyRecordReduced,
    GDFIataTurbRecord,
    GDFLabelRecord,
    GDFLegendRecord,
    GDFLightningRecord,
    GDFMetarRecord,
    GDFNavInfo2Record,
    GDFNavInfoRecord,
    GDFPalletCode,
    GDFPirep2Record,
    GDFPirepRecord,
    GDFPoint,
    GDFPolyRecord,
    GDFRadarSummaryRecord,
    GDFRecordHeader,
    GDFRecordType,
    GDFRle1Record,
    GDFRvrElement,
    GDFRvrRecord,
    GDFSubRecordType,
    GDFTafRecord,
    GDFTapsPrivateRecord,
    GDFTapsRecord,
    GDFTitleRecord,
    GDFWindsCompactData,
    GDFWindsCompactRecord,
    GDFWindsRecord,
    GDFWindsSmallRecord,
    GDFWmoCatRecord,
    GDFWmoCloudRecord,
    GDFWmoHzdVolRecord,
    GDFWmoJetStreamPoint,
    GDFWmoJetStreamRecord,
    GDFWmoRadationRecord,
    GDFWmoStormRecord,
    GDFWmoTropRecord,
    GDFWmoVolcano2CloudRecord,
    GDFWmoVolcano2Record,
    GDFWmoVolcanoAshCloud,
    GDFWmoVolcanoRecord
} from './type';

const decoder = new TextDecoder();

export interface GDFParseResult {
    frames: GDFFrame[];
    isForecast: boolean;
    decompressedSize: number;
    rawLength: number;
}

export class GDFParser {
    parse(id: number, buffer: ArrayBuffer): GDFParseResult | null {
        try {
            const comp = new Uint8Array(buffer, 0, buffer.byteLength - 6);
            const decompressed = decompress(new Uint8Array(comp), { defaultHeapSize: 30e6 }).buffer;

            const frameDefinitions = readFrameDefinitions(new DataView(decompressed, 19));

            const frames: GDFFrame[] = [];
            let offset = 0;

            for (const frameDefinition of frameDefinitions.frames) {
                offset = frameDefinition.offset;
                const view = new DataView(decompressed, offset);
                const frame: GDFFrame = {
                    records: [],
                    header: parseHeader(view)
                };

                offset += 19;

                // Lightning records are cumulated into one to save on JS heap
                let lightningRecord: GDFLightningRecord | null = null;
                let lightningRecordIdx = 0;

                while (true) {
                    if (offset == decompressed.byteLength) {
                        break;
                    }

                    const recordView = new DataView(view.buffer, offset);
                    const recordDefinition = parseRecordDefinition(recordView);

                    if (recordDefinition.type === GDFRecordType.eof) {
                        break;
                    }

                    if (recordDefinition.type === GDFRecordType.hotpolyhi) {
                        frame.records.push(...parseHotPolyHi(recordView));
                    } else if (recordDefinition.type === GDFRecordType.datetime2) {
                        frame.records.push(parseDateTime2(recordView));
                    } else if (recordDefinition.type === GDFRecordType.hotpoly) {
                        frame.records.push(...parseHotPoly(recordView));
                    } else if (recordDefinition.type === GDFRecordType.datetime) {
                        frame.records.push(praseDateTimeRecord(recordView));
                    } else if (recordDefinition.type === GDFRecordType.navinfo2) {
                        frame.records.push(parseNavInfo2(recordView));
                    } else if (recordDefinition.type === GDFRecordType.rle1) {
                        frame.records.push(parseRle(recordView));
                    } else if (recordDefinition.type === GDFRecordType.navinfo) {
                        frame.records.push(parseNavInfo(recordView));
                    } else if (recordDefinition.type === GDFRecordType.wmocloud) {
                        frame.records.push(parseWmoCloudRecord(recordView));
                    } else if (recordDefinition.type === GDFRecordType.wmohzdvol) {
                        frame.records.push(parseHzdVolcanoRecord(recordView));
                    } else if (recordDefinition.type === GDFRecordType.legend) {
                        frame.records.push(parseLegendRecord(recordView));
                    } else if (recordDefinition.type === GDFRecordType.title) {
                        frame.records.push(parseTitleRecord(recordView));
                    } else if (recordDefinition.type === GDFRecordType.winds) {
                        frame.records.push(parseWindsRecord(recordView));
                    } else if (recordDefinition.type === GDFRecordType.windssm) {
                        frame.records.push(parseWindsSmallRecord(recordView));
                    } else if (recordDefinition.type === GDFRecordType.metar) {
                        frame.records.push(parseMetarRecord(recordView));
                    } else if (recordDefinition.type === GDFRecordType.taf) {
                        frame.records.push(parseTafRecord(recordView));
                    } else if (recordDefinition.type === GDFRecordType.ccfp) {
                        frame.records.push(parseCcfpRecord(recordView));
                    } else if (recordDefinition.type === GDFRecordType.delay) {
                        frame.records.push(parseDelayRecord(recordView));
                    } else if (recordDefinition.type === GDFRecordType.rvr) {
                        frame.records.push(parseRvrRecord(recordView));
                    } else if (recordDefinition.type === GDFRecordType.diversion) {
                        frame.records.push(parseDiversionRecord(recordView));
                    } else if (recordDefinition.type === GDFRecordType.label) {
                        frame.records.push(parseLabelRecord(recordView));
                    } else if (recordDefinition.type === GDFRecordType.pirep2) {
                        frame.records.push(parsePirep2Record(recordView));
                    } else if (recordDefinition.type === GDFRecordType.pirep) {
                        frame.records.push(parsePirepRecord(recordView));
                    } else if (recordDefinition.type === GDFRecordType.fca) {
                        frame.records.push(parseFcaRecord(recordView));
                    } else if (recordDefinition.type === GDFRecordType.radsum) {
                        frame.records.push(parseRadarSummaryRecord(recordView));
                    } else if (recordDefinition.type === GDFRecordType.custom) {
                        frame.records.push(parseCustomRecord(recordView));
                    } else if (recordDefinition.type === GDFRecordType.iataturb) {
                        frame.records.push(parseIataTurbRecord(recordView));
                    } else if (recordDefinition.type === GDFRecordType.windscompact) {
                        frame.records.push(parseWindsCompactRecord(recordView));
                    } else if (recordDefinition.type === GDFRecordType.wmovolcano) {
                        frame.records.push(parseWmoVolcanoRecord(recordView));
                    } else if (recordDefinition.type === GDFRecordType.poly) {
                        frame.records.push(...parsePolyRecord(recordView));
                    } else if (recordDefinition.type === GDFRecordType.lightning) {
                        if (!lightningRecord) {
                            // If it's the first lightning record
                            let tmpOffset = offset;
                            let lightningCount = 0;
                            while (true) {
                                if (view.getUint8(tmpOffset) === GDFRecordType.eof) {
                                    break;
                                }

                                if (view.getUint8(tmpOffset) === GDFRecordType.lightning) {
                                    lightningCount++;
                                }

                                tmpOffset += view.getUint16(tmpOffset + 1, true);
                            }
                            lightningRecord = {
                                type: GDFRecordType.lightning,
                                points: new Float32Array(lightningCount * 2),
                                timeDev: new Int16Array(lightningCount),
                                amps: new Int8Array(lightningCount),
                                strokes: new Uint8Array(lightningCount),
                                count: lightningCount
                            };
                            frame.records.push(lightningRecord);
                        }
                        parseLightingRecord(lightningRecord, lightningRecordIdx++, recordView);
                    } else if (recordDefinition.type === GDFRecordType.wmostorm) {
                        frame.records.push(parseWmoStormRecord(recordView));
                    } else if (recordDefinition.type === GDFRecordType.wmotrop) {
                        frame.records.push(parseWmoTropRecord(recordView));
                    } else if (recordDefinition.type === GDFRecordType.wmocat) {
                        frame.records.push(parseWmoCatRecord(recordView));
                    } else if (recordDefinition.type === GDFRecordType.wmojetstream) {
                        frame.records.push(parseWmoJetStreamRecord(recordView));
                    } else if (recordDefinition.type === GDFRecordType.front) {
                        frame.records.push(parseFrontRecord(recordView));
                    } else if (recordDefinition.type === GDFRecordType.wmohzdrad) {
                        frame.records.push(parseWmoRadiationRecord(recordView));
                    } else if (recordDefinition.type === GDFRecordType.taps) {
                        frame.records.push(parseTapsRecord(recordView));
                    } else if (recordDefinition.type === GDFRecordType.wmovolcano2) {
                        frame.records.push(parseWmoVolcano2Record(recordView));
                    } else {
                        // Frames definitions will be part of the first frame of in the file
                        // Don't include them in the record list because we've already parsed them
                        if (recordDefinition.type !== GDFRecordType.frames) {
                            frame.records.push({ type: recordDefinition.type });
                        }
                    }

                    offset += recordDefinition.length;
                }

                // Convective sigmets products that have wrong palette code, override it here.
                if (id === 0x6085 || id === 0x6082) {
                    frame.header.productPallet = GDFPalletCode.csigmets;
                } else if (id === 0x6087 || id === 0x608a) {
                    frame.header.productPallet = GDFPalletCode.volcash;
                } else if (id === 0x601e || id === 0x601f || id === 0x6012) {
                    frame.header.productPallet = GDFPalletCode.taps;
                }
                frames.push(frame);
            }
            return {
                frames,
                decompressedSize: decompressed.byteLength,
                isForecast: frameDefinitions.hasInternalFrameRecord,
                rawLength: buffer.byteLength
            } satisfies GDFParseResult;
        } catch (err) {
            logger.warn('Error parsing GDF', err);
            return null;
        }
    }
}

function parseHeader(view: DataView): GDFHeader {
    const flags = view.getUint32(15, true);
    const type = flags & 0x00000007;
    const pallet = flags & 0x000003f8;

    return {
        //FEGDF
        version: view.getUint16(5, true),
        productId: view.getUint16(7, true),
        latNorth: view.getUint8(9),
        latSouth: view.getUint8(10),
        lonEast: view.getUint8(11),
        lonWest: view.getUint8(12),
        maxPoints: view.getUint16(13, true),
        productType: type,
        productPallet: pallet
    };
}

function parseRecordDefinition(view: DataView): GDFRecordHeader {
    return {
        type: view.getUint8(0),
        length: view.getUint16(1, true)
    };
}

function readFrameDefinitions(view: DataView): { frames: GDFFrameDefinitionRecord[]; hasInternalFrameRecord: boolean } {
    const recordHeader = parseRecordDefinition(view);
    const frames: GDFFrameDefinitionRecord[] = [];
    let hasFrameRecord = false;

    // This frame is optional, it might not be in the GDF
    if (recordHeader.type === GDFRecordType.frames) {
        const frameCount = view.getUint8(3);
        for (let i = 0; i < frameCount; i++) {
            frames.push({
                sequence: view.getUint8(4 + i * 5),
                offset: view.getUint32(5 + i * 5, true)
            });
        }
        hasFrameRecord = true;
    } else {
        // In that case just make one up
        frames.push({
            sequence: 0,
            offset: 0
        });
        hasFrameRecord = false;
    }
    return { frames, hasInternalFrameRecord: hasFrameRecord };
}

function parseDateTime2(view: DataView): GDFDateTime2Record {
    const validFrom = parseDateTime(view, 3);
    const validTo = parseDateTime(view, 10);
    const createdAt = parseDateTime(view, 17);
    return {
        type: GDFRecordType.datetime2,
        validFrom,
        validTo,
        createdAt
    };
}

function praseDateTimeRecord(view: DataView): GDFDateTimeRecord {
    return {
        type: GDFRecordType.datetime,
        time: parseDateTime(view, 3)
    };
}

function parseRle(view: DataView): GDFRle1Record {
    const blockLength = view.getUint16(1, true);
    const line = view.getUint16(3, true);
    const start = view.byteOffset + 5;
    const data = view.buffer.slice(start, start + blockLength - 5);

    return {
        type: GDFRecordType.rle1,
        line: line,
        data: data
    };
}

const hotpolySeverityMultipliers = {
    [GDFHotPollyTypes.turbulence_edr]: 0.1,
    [GDFHotPollyTypes.turbulence_gtgnowcast]: 0.1,
    [GDFHotPollyTypes.sehzd_turbulence]: 0.1,
    [GDFHotPollyTypes.sehzd_tstorm]: 0.2
};

const reducedHotPolyHiParse: GDFHotPollyTypes[] = [
    GDFHotPollyTypes.turbulence_edr,
    GDFHotPollyTypes.turbulence_cat,
    GDFHotPollyTypes.turbulence_gtgnowcast,
    GDFHotPollyTypes.sehzd_turbulence,
    GDFHotPollyTypes.icing_new,
    GDFHotPollyTypes.sehzd_icing,
    GDFHotPollyTypes.hiwc,
    GDFHotPollyTypes.cumulonimbus_tops,
    GDFHotPollyTypes.cumulonimbus_extents,
    GDFHotPollyTypes.sehzd_tstorm,
    GDFHotPollyTypes.contrail
];

function parseHotPolyHi(view: DataView): GDFHotPolyHiRecord[] {
    const subType: GDFHotPollyTypes = view.getUint8(5);
    if (reducedHotPolyHiParse.includes(subType)) {
        return parseHotPolyHiReduced(view);
    }
    return [parseHotPolyHiNormal(view)];
}

function parseHotPolyHiNormal(view: DataView): GDFHotPolyHiRecordNormal {
    const pointsCount = view.getUint16(26, true);
    const shape = view.getUint8(4) as GDFHotPollyShape;
    const isPolygon = shape === GDFHotPollyShape.polygon;
    const labelLen = view.getUint8(28);
    const detailLen = view.getUint16(29, true);
    let offset = 63 + pointsCount * 8;
    const label = parseString(view, offset, labelLen);
    offset += labelLen;
    const detail = parseString(view, offset, detailLen);
    const polyType: GDFHotPollyTypes = view.getUint8(5);
    const iSpeedDirBaseLat100K = view.getInt32(52, true);
    const iSpeedDirBaseLng100K = view.getInt32(56, true);
    const hasSpeedDirBasePoint = polyType === GDFHotPollyTypes.probsevere;

    return {
        type: GDFRecordType.hotpolyhi,
        reduced: false,
        flags: view.getUint8(3),
        shape,
        polyType: polyType,
        startTime: parseDateTime(view, 6),
        endTime: parseDateTime(view, 13),
        id: valueOrNull(view.getUint16(20, true), 0xffff),
        tops: valueOrNull(view.getUint8(22), 0xff),
        speed: valueOrNull(view.getUint8(23), 0xff),
        dir: valueOrNull(view.getUint16(24, true), 0xffff),
        issuedTime: parseDateTime(view, 31),
        subType: view.getUint8(38),
        severity: view.getUint8(39),
        base: valueOrNull(view.getUint8(40), 0xff),
        coverage: view.getUint8(41),
        outlook: view.getUint8(42),
        suaBase: valueOrNull(view.getUint16(43, true), 0xffff),
        suaTop: valueOrNull(view.getUint16(45, true), 0xffff),
        probSevere: valueOrNull(view.getUint8(47), 0xff),
        probHail: valueOrNull(view.getUint8(48), 0xff),
        probWind: valueOrNull(view.getUint8(49), 0xff),
        probTornado: valueOrNull(view.getUint8(50), 0xff),
        probLightning: valueOrNull(view.getUint8(51), 0xff),
        speedDirBasePoint: hasSpeedDirBasePoint ? [iSpeedDirBaseLng100K / 100_000, iSpeedDirBaseLat100K / 100_000] : null,
        points: parsePoints(view, 63, pointsCount, isPolygon),
        detail: detail,
        label: label
    };
}

function parseHotPolyHiReduced(view: DataView): GDFHotPolyHiRecordReduced[] {
    //const flags = view.getUint8(3);
    //const shape: GDFHotPollyShape = view.getUint8(4);
    const type: GDFHotPollyTypes = view.getUint8(5);
    //const startTime = parseDateTime(view, 6);
    //const endTime = parseDateTime(view, 13);
    const id = view.getUint16(20, true);
    //const tops = view.getUint8(22);
    //const speedOrBase = view.getUint8(23);
    //const direction = view.getUint16(24, true);
    const pointsCount = view.getUint16(26, true);
    //const labelLength = view.getUint8(28);
    //const detailsLength = view.getUint16(29, true);
    //const issueTime = parseDateTime(view, 31);
    //const subType = view.getUint8(38);
    let severity = view.getUint8(39);
    //const altitudeBase = view.getUint8(40);
    //const coverage = view.getUint8(41);
    //const outlook = view.getUint8(42);
    //const suaFLBase = view.getUint16(44, true);
    //const suaFLTop = view.getUint16(46, true);

    if (type === GDFHotPollyTypes.contrail) {
        // Contrails have severity in the ID field for whatever reason...
        severity = id;
    }
    // Reserved 16

    if (type in hotpolySeverityMultipliers) {
        severity *= hotpolySeverityMultipliers[type as keyof typeof hotpolySeverityMultipliers];
    }

    const polygon: GDFPoint[] = [];

    for (let i = 0; i < pointsCount; i++) {
        // Need this because maplibre doesn't render thing correctly past -85, 85 latitude
        // NOTE: technically we're altering the data here and creating an incorrect data point but it's the best we can do
        const lng = view.getInt32(63 + i * 8, true) / 10_000;
        const lat = view.getInt32(67 + i * 8, true) / 10_000;
        const latClamped = Math.min(85, Math.max(-85, lat));
        polygon.push([lng, latClamped]);
    }

    // Close the polygon
    polygon.push([...polygon[0]]);

    const polygons = normalizePolygon(polygon);

    return polygons.map((points) => ({
        type: GDFRecordType.hotpolyhi,
        polyType: type,
        reduced: true,
        points,
        severity: Math.round(severity), // Forces the severity to be a SMI
        bbox: new Float32Array(getBbox(points))
    }));
}

const reducedHotPolyParse: GDFHotPollyTypes[] = [GDFHotPollyTypes.nexrad, GDFHotPollyTypes.precip];

function parseHotPoly(view: DataView): GDFHotPolyRecord[] {
    const subType: GDFHotPollyTypes = view.getUint8(5);
    if (reducedHotPolyParse.includes(subType)) {
        return parseHotPolyReduced(view);
    }
    return parseHotPolyNormal(view);
}

function parseHotPolyNormal(view: DataView): GDFHotPolyRecordNormal[] {
    const shape = view.getUint8(4) as GDFHotPollyShape;
    const pointsLen = view.getUint16(26, true);
    const labelLen = view.getUint8(28);
    const detailLen = view.getUint16(29, true);

    const points: GDFPoint[] = [];
    for (let i = 0; i < pointsLen; i++) {
        points.push(parseGEO_COORD_INT(new DataView(view.buffer, view.byteOffset + 31 + i * 5)));
    }

    if (shape === GDFHotPollyShape.polygon) {
        // close the polygon
        points.push([...points[0]]);
    }

    let offset = view.byteOffset + 31 + pointsLen * 5;
    const label = decoder.decode(view.buffer.slice(offset, offset + labelLen) as ArrayBuffer);
    offset += labelLen;
    const detail = decoder.decode(view.buffer.slice(offset, offset + detailLen) as ArrayBuffer);

    const result: GDFHotPolyRecordNormal[] = [
        {
            type: GDFRecordType.hotpoly,
            reduced: false,
            flags: view.getUint8(3),
            shape: shape,
            polyType: view.getUint8(5),
            startTime: parseDateTime(view, 6),
            endTime: parseDateTime(view, 13),
            id: view.getUint16(20, true),
            tops: view.getUint8(22),
            speed: view.getUint8(23),
            dir: view.getUint16(24, true),
            label: label,
            detail: detail,
            points: points
        }
    ];

    if (result[0].polyType === GDFHotPollyTypes.trop_height) {
        // Duplicate record in point format to make it easier to render
        result.push({
            ...result[0],
            shape: GDFHotPollyShape.point,
            points: [result[0].points[0]]
        });
    }

    return result;
}

function parseHotPolyReduced(view: DataView): GDFHotPolyRecordReduced[] {
    const pointsCount = view.getUint16(26, true);
    const polyType: GDFHotPollyTypes = view.getUint8(5);

    const polygon: GDFPoint[] = [];
    for (let i = 0; i < pointsCount; i++) {
        polygon.push(parseGEO_COORD_INT(new DataView(view.buffer, view.byteOffset + 31 + i * 5, 5)));
    }

    // Close the polygon
    if (polygon[0][0] !== polygon[pointsCount - 1][0] || polygon[0][1] !== polygon[pointsCount - 1][1]) {
        polygon.push([...polygon[0]]);
    }

    const normalized = normalizePolygon(polygon);

    return normalized.map(
        (polygon) =>
            ({
                type: GDFRecordType.hotpoly,
                reduced: true,
                points: polygon,
                polyType,
                bbox: new Float32Array(getBbox(polygon)),
                severity: view.getUint16(20, true)
            }) satisfies GDFHotPolyRecordReduced
    );
}

function arePointsCollinear(pointA: number[], pointB: number[], pointC: number[]) {
    const [x1, y1] = pointA;
    const [x2, y2] = pointB;
    const [x3, y3] = pointC;

    const area = x1 * (y2 - y3) + x2 * (y3 - y1) + x3 * (y1 - y2);

    // If area is 0 (or near 0 due to floating point precision), points are collinear
    return Math.abs(area) < Number.EPSILON;
}

function normalizePolygon(polygon2: GDFPoint[]): Float32Array[] {
    let previous = polygon2[0];
    let current = polygon2[1];
    let next = polygon2[2];
    const polygon: GDFPoint[] = [previous];
    for (let i = 1; i < polygon2.length - 1; i++) {
        previous = polygon2[i - 1];
        current = polygon2[i];
        next = polygon2[i + 1];

        if (!arePointsCollinear(previous, current, next)) {
            polygon.push(current);
        }
    }

    polygon.push(polygon2[polygon2.length - 1]);

    const polygons: Float32Array[] = [];
    const crossesToTheLeft = polygon.some((x) => x[0] < -180);
    const crossesToTheRight = polygon.some((x) => x[0] > 180);

    if (crossesToTheLeft || crossesToTheRight) {
        let clipped = clipPolygon(polygon, [-180, -90, 180, 90]);
        polygons.push(new Float32Array(clipped.flat()));

        if (crossesToTheLeft) {
            clipped = clipPolygon(polygon, [-540, -90, -180, 90]);
            if (clipped.length > 0) {
                for (let i = 0; i < clipped.length; i++) {
                    clipped[i][0] += 360;
                }
                polygons.push(new Float32Array(clipped.flat()));
            }
        }

        if (crossesToTheRight) {
            clipped = clipPolygon(polygon, [180, -90, 540, 90]);
            if (clipped.length > 0) {
                for (let i = 0; i < clipped.length; i++) {
                    clipped[i][0] -= 360;
                }
                polygons.push(new Float32Array(clipped.flat()));
            }
        }
    } else {
        polygons.push(new Float32Array(polygon.flat()));
    }
    return polygons;
}

function parseNavInfo2(view: DataView): GDFNavInfo2Record {
    return {
        type: GDFRecordType.navinfo2,
        width: view.getUint16(3, true),
        height: view.getUint16(5, true),
        lonLeft: view.getInt32(7, true) / 10_000,
        lonRight: view.getInt32(11, true) / 10_000,
        latTop: view.getInt32(15, true) / 10_000,
        latBottom: view.getInt32(19, true) / 10_000
    };
}

function parseNavInfo(view: DataView): GDFNavInfoRecord {
    return {
        type: GDFRecordType.navinfo,
        lines: view.getUint16(3, true),
        elements: view.getUint16(5, true),
        centerLon: view.getFloat32(7, true),
        latTop: view.getFloat32(11, true),
        centerLat: view.getFloat32(15, true),
        degreesPerLine: view.getFloat32(19, true),
        degreesPerElement: view.getFloat32(23, true)
    };
}

function parseWmoCloudRecord(view: DataView): GDFWmoCloudRecord {
    const pointsCount = view.getUint16(3, true);
    const points = parsePoints(view, 40, pointsCount, false);
    return {
        type: GDFRecordType.wmocloud,
        points: points,
        cloudBase: valueOrNull(view.getUint16(7, true), 0xffff),
        cloudTops: valueOrNull(view.getUint16(9, true), 0xffff),
        cloudType: view.getUint8(11),
        dist: view.getUint8(12),
        turbLevel: view.getUint8(13),
        turbBase: view.getUint16(14, true),
        turbTops: view.getUint16(16, true),
        iceLevel: view.getUint8(18),
        iceBase: view.getUint16(19, true),
        iceTops: view.getUint16(21, true),
        shape: view.getUint8(23),
        label: parseString(view, 40 + pointsCount * 8, view.getUint16(5, true)),
        bbox: getBboxFromPoints(points)
    };
}

function parseHzdVolcanoRecord(view: DataView): GDFWmoHzdVolRecord {
    const point: GDFPoint = [view.getInt32(7, true) / 10_000, view.getInt32(3, true) / 10_000];
    const time = parseDateTime(view, 11);
    const decoder = new TextDecoder();
    const length = view.getUint8(18);
    const name = decoder.decode(view.buffer.slice(view.byteOffset + 19, view.byteOffset + 19 + length) as ArrayBuffer);
    return {
        type: GDFRecordType.wmohzdvol,
        point,
        time,
        name
    };
}

function parseFrontRecord(view: DataView): GDFFrontRecord {
    return {
        type: GDFRecordType.front,
        frontType: view.getUint8(3),
        id: valueOrNull(view.getUint16(4, true), 0xffff),
        points: parsePoints(view, 8, view.getUint16(6, true), false)
    };
}

function parseLegendRecord(view: DataView): GDFLegendRecord {
    const decoder = new TextDecoder();
    const length = view.getUint16(1, true) - 3;
    return {
        type: GDFRecordType.legend,
        text: decoder.decode(view.buffer.slice(view.byteOffset + 3, view.byteOffset + length + 3) as ArrayBuffer)
    };
}

function parseTitleRecord(view: DataView): GDFTitleRecord {
    const decoder = new TextDecoder();
    const length = view.getUint16(1, true) - 3;
    return {
        type: GDFRecordType.title,
        text: decoder.decode(view.buffer.slice(view.byteOffset + 3, view.byteOffset + length + 3) as ArrayBuffer)
    };
}

function parseWindsRecord(view: DataView): GDFWindsRecord {
    const point: GDFPoint = [view.getInt32(7, true) / 10_000, view.getInt32(3, true) / 10_000];
    return {
        type: GDFRecordType.winds,
        point: point,
        direction: view.getUint16(11, true),
        speed: view.getUint8(13),
        useColorSpeed: view.getUint8(14),
        colorSpeed: view.getUint32(15, true),
        tropopause: view.getUint16(19, true) * 100,
        useColorTrop: view.getUint8(21),
        colorTrop: view.getUint32(22, true),
        temperature: view.getInt8(26),
        userColorTemp: view.getUint8(27),
        colorTemp: view.getUint32(28, true),
        altitude: view.getUint16(32, true) * 100,
        useColorAlt: view.getUint8(34),
        colorAlt: view.getUint32(36, true)
    };
}

function parseWindsCompactRecord(view: DataView): GDFWindsCompactRecord {
    const topLeft: GDFPoint = [view.getInt32(9, true) / 100_000, view.getInt32(5, true) / 100_000];

    return {
        type: GDFRecordType.windscompact,
        altitude: view.getUint16(3, true),
        topLeft: topLeft,
        latResolution: view.getUint32(13, true) / 100_000,
        lonResolution: view.getUint32(17, true) / 100_000,
        gridWidth: view.getUint16(21, true),
        gridHeight: view.getUint16(23, true),
        dataType: 'wind' as const,
        data: parseWindsCompactData(view)
    };
}

function parseWmoVolcanoAshCloud(mainView: DataView, offset: number): GDFWmoVolcanoAshCloud {
    const view = new DataView(mainView.buffer, mainView.byteOffset + offset);
    const points = view.getUint16(8, true);
    const byteOffset = view.getUint16(10, true);
    return {
        type: GDFSubRecordType.wmoAshCloud,
        interval: view.getUint8(0),
        dir: view.getUint16(1, true),
        speed: view.getUint8(3),
        base: valueOrNull(view.getUint16(4, true), 0xffff),
        tops: valueOrNull(view.getUint16(6, true), 0xffff),
        points: parsePoints(mainView, byteOffset, points, true)
    };
}

function parseWmoVolcanoRecord(view: DataView): GDFWmoVolcanoRecord {
    const propertiesLen = view.getUint16(11, true);
    const propertiesByteOffset = view.getUint16(13, true);

    return {
        type: GDFRecordType.wmovolcano,
        point: [view.getInt32(7, true) / 10_000, view.getInt32(3, true) / 10_000],
        name: parseString(view, 15, 23),
        id: parseString(view, 38, 7),
        summit: valueOrNull(view.getUint16(45, true), 0xffff),
        color: view.getUint8(47),
        reportTime: parseDateTime(view, 48),
        nextReportTime: parseDateTime(view, 55),
        advisory: parseString(view, 62, 9),
        details: parseString(view, 71, 64).replaceAll('/n', '').replaceAll('/r', ''), // someone genius put /r/n in the string
        clouds: [
            parseWmoVolcanoAshCloud(view, 135 + 12 * 0),
            parseWmoVolcanoAshCloud(view, 135 + 12 * 1),
            parseWmoVolcanoAshCloud(view, 135 + 12 * 2),
            parseWmoVolcanoAshCloud(view, 135 + 12 * 3)
        ],
        properties: parseString(view, propertiesByteOffset, propertiesLen)
    };
}

function parsePolyRecord(view: DataView): GDFPolyRecord[] {
    const result: GDFPolyRecord[] = [];
    const length = view.getUint16(8, true);

    for (let i = 0; i < length; i++) {
        const offset = 10 + i * 5;
        const wLongitude = view.getUint16(offset, true);
        const wLatitude = view.getUint16(offset + 2, true);

        const tmp4 = view.getUint8(offset + 4);
        const cLonUpper4 = tmp4 & 0xf;
        let cLatUpper4 = (tmp4 >>> 4) & 0xf;

        // new poly
        if (cLatUpper4 & 0x08) {
            cLatUpper4 &= ~0x08;

            if (result.length !== 0) {
                const previousPoly = result[result.length - 1];

                const first = previousPoly.points[0];
                const last = previousPoly.points[previousPoly.points.length - 1];
                let shape: GDFHotPollyShape;
                if (first[0] !== last[0] || first[1] !== last[1]) {
                    shape = GDFHotPollyShape.line;
                } else {
                    shape = GDFHotPollyShape.polygon;
                }
                previousPoly.shape = shape;
            }

            result.push({
                type: GDFRecordType.poly,
                shape: GDFHotPollyShape.line,
                points: []
            });
        }

        result[result.length - 1].points.push([
            (cLonUpper4 * 65_536 + wLongitude - 180_000) / 1_000,
            (cLatUpper4 * 65_536 + wLatitude - 180_000) / 1_000
        ]);
    }

    return result;
}

function parseWindsCompactData(view: DataView): GDFWindsCompactData {
    // Winds compact data record header has 26 bytes
    const dataView = new DataView(view.buffer, view.byteOffset + 26);
    const dataRecordLength = 3; // 3 bytes per record (u, v, temperature)
    const dataLength = view.getUint16(21, true) * view.getUint16(23, true) * dataRecordLength;
    const recNum = dataLength / dataRecordLength;

    const temperature = new Int8Array(recNum);
    const speed = new Uint8Array(recNum);
    const direction = new Int16Array(recNum);

    for (let i = 0; i < recNum; i++) {
        const offset = i * dataRecordLength;
        const uint = dataView.getInt8(offset);
        const vint = dataView.getInt8(offset + 1);
        direction[i] = Math.round(Math.atan2(-uint, -vint) * (180 / Math.PI));
        speed[i] = Math.round(Math.max(0, Math.min(Math.sqrt(uint * uint + vint * vint), 128) * MS_TO_KT));
        temperature[i] = dataView.getInt8(offset + 2);
    }

    return {
        length: recNum,
        temperature,
        speed,
        direction
    };
}

function parseWindsSmallRecord(view: DataView): GDFWindsSmallRecord {
    return {
        type: GDFRecordType.windssm,
        point: [view.getInt32(7, true) / 10000, view.getInt32(3, true) / 10000],
        direction: view.getInt16(11, true),
        speed: view.getUint8(13),
        temperature: view.getInt8(14),
        altitude: view.getUint16(16, true)
    };
}

function parseMetarRecord(view: DataView): GDFMetarRecord {
    const isSpecial = view.getUint8(17);
    const textLen = view.getUint8(48);
    return {
        type: GDFRecordType.metar,
        id: decoder.decode(view.buffer.slice(view.byteOffset + 3, view.byteOffset + 7) as ArrayBuffer),
        class: view.getUint8(7),
        point: [view.getInt32(12, true) / 10000, view.getInt32(8, true) / 10000],
        isDecoded: view.getUint8(16) !== 0,
        isSpecial: isSpecial === 0 ? null : isSpecial === 1 ? false : true,
        subType: String.fromCharCode(view.getUint8(18)) as GDFMetarRecord['subType'],
        date: parseDateTime(view, 19),
        flightCategory: view.getUint8(26),
        windDir: valueOrNull(view.getUint16(27, true), 0xffff),
        windVarStart: valueOrNull(view.getUint16(29, true), 0xffff),
        windVarStop: valueOrNull(view.getUint16(31, true), 0xffff),
        windSpeed: valueOrNull(view.getUint8(33), 0xff),
        windGusts: valueOrNull(view.getUint8(34), 0xff),
        visLessGrt: valueCharOrNull(view.getUint8(35), 0xff) as GDFMetarRecord['visLessGrt'],
        visibility: valueOrNull(view.getUint16(36, true), 0xffff, 0.01),
        visHeading: valueOrNull(view.getUint16(38, true), 0xffff),
        temperature: valueOrNull(view.getInt8(40), 127),
        dewPoint: valueOrNull(view.getInt8(41), 127),
        altimeter: valueOrNull(view.getUint16(42, true), 0xffff, 0.01),
        ceiling: valueOrNull(view.getUint16(44, true), 0xffff, 100),
        skyCoverage: valueOrNull(view.getUint8(46), 0xffff),
        text: decoder.decode(view.buffer.slice(view.byteOffset + 53, view.byteOffset + 53 + textLen) as ArrayBuffer)
    };
}

function parseTafRecord(view: DataView): GDFTafRecord {
    let text: string | null = null;
    const flags = view.getUint8(17);
    const rawData = (flags & 0x04) !== 0;
    const visualLessGt = (flags >> 3) & 0x03;
    if (rawData) {
        const length = view.getUint16(1, true);
        text = decoder.decode(view.buffer.slice(view.byteOffset + 23, view.byteOffset + length) as ArrayBuffer);
    }
    return {
        type: GDFRecordType.taf,
        id: decoder.decode(view.buffer.slice(view.byteOffset + 3, view.byteOffset + 7) as ArrayBuffer).trim(),
        point: [view.getInt32(11, true) / 10000, view.getInt32(7, true) / 10000],
        flightCategory: view.getUint8(15),
        windSpeed: valueOrNull(view.getUint8(16), 0xff),
        storm: (flags & 0x01) !== 0,
        stormVicinity: (flags & 0x02) !== 0,
        visualLessGt: visualLessGt === 0 ? null : visualLessGt === 1 ? 'G' : 'L',
        ceiling: valueOrNull(view.getUint16(18, true), 0xffff),
        visibility: valueOrNull(view.getUint16(20, true), 0xffff),
        text: text
    };
}

function parseCcfpRecord(view: DataView): GDFCcfpRecord {
    const pointsCount = view.getUint16(11, true);
    const points: GDFPoint[] = [];
    for (let i = 0; i < pointsCount; i++) {
        points.push(parseGEO_COORD_INT(new DataView(view.buffer, view.byteOffset + 13 + i * 5)));
    }

    // Close the polygon
    points.push([...points[0]]);

    return {
        type: GDFRecordType.ccfp,
        geometryType: view.getUint8(3),
        coverage: view.getUint8(4),
        prob: view.getUint8(5),
        growth: view.getUint8(6),
        tops: view.getUint8(7),
        speed: view.getUint8(8),
        direction: view.getUint16(9, true),
        points: points
    };
}

function parseDelayElement(view: DataView): GDFDelayElement {
    return {
        type: String.fromCharCode(view.getUint8(0)) as GDFDelayElement['type'],
        createTime: parseSystemTime(view, 1),
        updateTime: parseSystemTime(view, 17),
        startTime: parseSystemTime(view, 33),
        stopTime: parseSystemTime(view, 49),
        delayMinutesMinAvg: view.getUint16(65, true),
        delayMinutesMax: view.getUint16(67, true),
        trend: view.getUint8(69),
        reason: parseString(view, 70, 50)
    };
}

function parseDelayRecord(view: DataView): GDFDelayRecord {
    const airportId = decoder.decode(view.buffer.slice(view.byteOffset + 3, view.byteOffset + 7) as ArrayBuffer).trim();
    const delayClass = view.getUint8(4);
    const point: GDFPoint = [view.getInt32(12, true) / 10000, view.getInt32(8, true) / 10000];
    const count = view.getUint8(16);
    const elements: GDFDelayElement[] = [];

    for (let i = 0; i < count; i++) {
        elements.push(parseDelayElement(new DataView(view.buffer, view.byteOffset + 17 + i * 120)));
    }

    // delay element rec has a fixed size reason field (50 chars), if the reason exceeds 50, there's an array of offsets at the end
    // of delay record with offset for the rest of the text ...

    let offset = 17 + elements.length * 120 + elements.length * 2;
    for (let i = 0; i < elements.length; i++) {
        const count = view.getUint16(17 + elements.length * 120 + i * 2, true);
        if (count) {
            elements[i].reason += parseString(view, offset, count);
            offset += count;
        }
    }

    // Calculate level for display
    let level = 0;
    let closureType = 0;
    for (const element of elements) {
        switch (element.type) {
            case 'A':
                // Arrival delay
                if (element.delayMinutesMax > 45) {
                    level = Math.max(level, 2);
                } else {
                    level = Math.max(level, 1);
                }
                break;

            case 'D':
                // Departure delay
                if (element.delayMinutesMax > 45) {
                    level = Math.max(level, 2);
                } else {
                    level = Math.max(level, 1);
                }
                break;

            case 'G':
                // Ground delay
                level = Math.max(level, 3);
                break;

            case 'P':
                // Ground stop
                level = Math.max(level, 3);
                break;

            case 'C':
                // Airport closure
                level = Math.max(level, 4);
                closureType = element.trend;
                break;
        }
    }

    return {
        type: GDFRecordType.delay,
        airportId: airportId,
        class: delayClass,
        point: point,
        elements: elements,
        level: level,
        trend: closureType
    };
}

function parseRvrElement(view: DataView): GDFRvrElement {
    return {
        runway: decoder.decode(view.buffer.slice(view.byteOffset, view.byteOffset + 3) as ArrayBuffer).trim(),
        touchDownVisibility: view.getUint8(3),
        midpointVisibility: view.getUint8(4),
        rollOutVisibility: view.getUint8(5),
        touchDownVisibilityTrend: view.getUint8(6),
        midpointVisibilityTrend: view.getUint8(7),
        rolloutVisibilityTrend: view.getUint8(8),
        lightsEdge: view.getUint8(9),
        lightsCenter: view.getUint8(10)
    };
}

function parseRvrRecord(view: DataView): GDFRvrRecord {
    const airportId = decoder.decode(view.buffer.slice(view.byteOffset + 3, view.byteOffset + 7) as ArrayBuffer).trim();
    const _class = view.getUint8(7);
    const point: GDFPoint = [view.getInt32(12, true) / 10000, view.getInt32(8, true) / 10000];
    const elementCount = view.getUint8(16);
    const elements: GDFRvrElement[] = [];
    let offset = view.byteOffset + 17;
    let minimumVisibility = 70;

    for (let i = 0; i < elementCount; i++) {
        const element = parseRvrElement(new DataView(view.buffer, offset, 11));
        elements.push(element);
        if (element.touchDownVisibility !== 0) {
            minimumVisibility = Math.min(minimumVisibility, element.touchDownVisibility);
        }

        if (element.midpointVisibility !== 0) {
            minimumVisibility = Math.min(minimumVisibility, element.midpointVisibility);
        }

        if (element.rollOutVisibility !== 0) {
            minimumVisibility = Math.min(minimumVisibility, element.rollOutVisibility);
        }
        offset += 11;
    }

    return {
        type: GDFRecordType.rvr,
        class: _class,
        airportId: airportId,
        point: point,
        elements: elements,
        minimumVisibility: minimumVisibility
    };
}

function parseDiversionElement(view: DataView): GDFDiversionElement {
    return {
        type: String.fromCharCode(view.getUint8(0)) as GDFDiversionElement['type'],
        acid: decoder.decode(view.buffer.slice(view.byteOffset + 1, view.byteOffset + 8) as ArrayBuffer).trim(),
        airportToFrom: decoder.decode(view.buffer.slice(view.byteOffset + 8, view.byteOffset + 12) as ArrayBuffer).trim(),
        point: [view.getInt32(16, true) / 10000, view.getInt32(12, true) / 10000],
        active: view.getUint8(20)
    };
}

function parseDiversionRecord(view: DataView): GDFDiversionRecord {
    const airportId = decoder.decode(view.buffer.slice(view.byteOffset + 3, view.byteOffset + 7) as ArrayBuffer).trim();
    const _class = view.getUint8(7);
    const point: GDFPoint = [view.getInt32(12, true) / 10000, view.getInt32(8, true) / 10000];
    const fromCount = view.getUint16(16, true);
    const fromTrend = view.getUint8(18);
    const toCount = view.getUint16(19, true);
    const toTrend = view.getUint8(21);
    const count = view.getUint16(22, true);
    const elements: GDFDiversionElement[] = [];
    let offset = view.byteOffset + 24;

    for (let i = 0; i < count; i++) {
        elements.push(parseDiversionElement(new DataView(view.buffer, offset, 21)));
        offset += 21;
    }

    return {
        type: GDFRecordType.diversion,
        airportId: airportId,
        class: _class,
        point: point,
        fromCount: fromCount,
        fromTrend: String.fromCharCode(fromTrend) as GDFDiversionRecord['fromTrend'],
        toCount: toCount,
        toTrend: String.fromCharCode(toTrend) as GDFDiversionRecord['toTrend'],
        elements: elements
    };
}
function parseLabelRecord(view: DataView): GDFLabelRecord {
    return {
        type: GDFRecordType.label,
        horizontalAlign: view.getUint8(3),
        verticalAlign: view.getUint8(4),
        coordType: view.getUint8(5),
        point: [view.getInt32(10, true) / 10_000, view.getInt32(6, true) / 10_000],
        latOffset: view.getInt8(14),
        lonOffset: view.getUint8(15),
        text: decoder.decode(view.buffer.slice(view.byteOffset + 48, view.byteOffset + view.getUint16(1, true)) as ArrayBuffer)
    };
}

function parsePirep2Record(view: DataView): GDFPirep2Record {
    const reportLength = view.getUint16(73, true);
    return {
        type: GDFRecordType.pirep2,
        time: parseDateTime(view, 3),
        id: view.getUint16(10, true),
        acid: decoder.decode(view.buffer.slice(view.byteOffset + 13, view.byteOffset + 20) as ArrayBuffer).trim(),
        acType: decoder.decode(view.buffer.slice(view.byteOffset + 20, view.byteOffset + 24) as ArrayBuffer).trim(),
        pointFrom: [view.getInt32(28, true) / 10_000, view.getInt32(24, true) / 10_000],
        pointTo: [view.getInt32(36, true) / 10_000, view.getInt32(32, true) / 10_000],
        flightLevelUpper: valueOrNull(view.getUint16(40, true), 0xffff),
        flightLevelLower: valueOrNull(view.getUint16(42, true), 0xffff),
        altitudeUpper: valueOrNull(view.getUint16(44, true), 0xffff),
        altitudeLower: valueOrNull(view.getUint16(46, true), 0xffff),
        windSpeed: valueOrNull(view.getUint8(48), 0xff),
        windDir: valueOrNull(view.getUint16(49, true), 0xffff),
        temp: valueOrNull(view.getInt8(51), 127),
        skyCover: valueOrNull(view.getUint8(52), 0),
        cloudBase: valueOrNull(view.getUint16(53, true), 0xffff),
        cloudTop: valueOrNull(view.getUint16(55, true), 0xffff),
        weather: valueOrNull(view.getUint8(57), 0xff),
        visibility: valueOrNull(view.getUint8(58), 0xff),
        icingSeverity: valueOrNull(view.getUint8(59), 0),
        icingDetails: view.getUint8(60) & 0x07,
        icingColor: view.getUint32(64, true),
        turbulenceSeverity: valueOrNull(view.getUint8(65), 0),
        turbulenceType: view.getUint16(66, true) & 0x07,
        turbulenceBaseDuration: (view.getUint16(66, true) >> 3) & 0x07,
        turbulenceWorseDuration: (view.getUint16(66, true) >> 6) & 0x07,
        turbulenceColor: view.getUint32(68, true),
        urgent: (view.getUint8(72) & 0x01) !== 0,
        llws: (view.getUint8(72) & 0x02) !== 0,
        report: decoder.decode(view.buffer.slice(view.byteOffset + 87, view.byteOffset + 87 + reportLength) as ArrayBuffer)
    };
}

function parsePirepRecord(view: DataView): GDFPirepRecord {
    const textLen = view.getUint16(43, true);
    return {
        type: GDFRecordType.pirep,
        point: [view.getInt32(7, true) / 10_000, view.getInt32(3, true) / 10_000],
        time: parseDateTime(view, 11),
        id: parseString(view, 18, 8),
        altitude: valueOrNull(view.getUint16(26, true), 0xffff),
        wind: valueOrNull(view.getUint8(28), 0xff),
        windDir: valueOrNull(view.getUint16(29, true), 0xffff),
        temp: valueOrNull(view.getInt8(31), 127),
        turbIntensity: valueOrNull(view.getUint8(32), 0xff),
        // Skip stuff
        icingIntensity: valueOrNull(view.getUint8(38), 0xff),
        // Skip stuff
        text: parseString(view, 59, textLen)
    };
}

function parseFcaRecord(view: DataView): GDFFcaRecord {
    const pointsCount = view.getUint16(46, true);
    const nameLen = view.getUint8(58);
    const nameOffset = 84 + pointsCount * 8;
    const labelLen = view.getUint8(59);
    const labelOffset = nameOffset + nameLen;
    const detailLen = view.getUint16(60, true);
    const detailOffset = labelOffset + labelLen;
    const filterLen = view.getUint16(62, true);
    const filterOffset = detailOffset + detailLen;

    const shape = view.getUint8(3) as GDFFcaShape;
    const isPolygon = shape === GDFFcaShape.poly;
    return {
        type: GDFRecordType.fca,
        shape: shape,
        subType: view.getUint8(4),
        fcaStartTime: parseDateTime(view, 5),
        fcaEndTime: parseDateTime(view, 12),
        afpStartTime: parseDateTime(view, 19),
        afpEndTime: parseDateTime(view, 26),
        flags: view.getUint8(33),
        lookAheadTime: view.getUint8(35),
        ceiling: valueOrNull(view.getUint16(36, true), 0xffff),
        floor: valueOrNull(view.getUint16(38, true), 0xffff),
        avgDelay: valueOrNull(view.getUint16(40, true), 0xffff),
        direction: valueOrNull(view.getUint16(42, true), 0xffff),
        speed: valueOrNull(view.getUint16(44, true), 0xffff),
        point: [view.getInt32(52, true) / 10_000, view.getInt32(48, true) / 10_000],
        radius: view.getUint16(56, true),
        points: parsePoints(view, 84, pointsCount, isPolygon),
        name: parseString(view, nameOffset, nameLen),
        label: parseString(view, labelOffset, labelLen),
        detail: parseString(view, detailOffset, detailLen),
        filter: parseString(view, filterOffset, filterLen)
    };
}

function parseRadarSummaryRecord(view: DataView): GDFRadarSummaryRecord {
    return {
        type: GDFRecordType.radsum,
        point: [view.getInt32(7, true) / 10_000, view.getInt32(3, true) / 10_000],
        tops: valueOrNull(view.getUint16(11, true), 0xffff),
        dir: valueOrNull(view.getUint16(13, true), 0xffff),
        speed: valueOrNull(view.getUint16(15, true), 0xffff),
        hailSize: valueOrNull(view.getUint8(17), 0xff),
        meso: (view.getUint8(18) & 0x01) !== 0,
        tvs: (view.getUint8(18) & 0x02) !== 0,
        zoom: view.getUint8(18) >>> 2,
        intensity: view.getUint8(19)
    };
}

function parseCustomRecord(view: DataView): GDFCustomRecord {
    const pointsCount = view.getUint16(3, true);
    const propertyLen = view.getUint16(5, true);
    const shape = view.getUint8(7) as GDFCustomDrawShape;
    const usePen = !!view.getUint8(8);
    const penWidth = view.getUint8(9);
    const penStyle = view.getUint8(10) as GDFCustomDrawPenStyle;
    const penColor = colorFromNumber(view.getUint32(11, true));

    const useFil = !!view.getUint8(15);
    const fillStyle = view.getUint8(16) as GDFCustomDrawFillStyle;
    const fillColor = colorFromNumber(view.getUint32(17));

    const headCap = view.getUint8(21) as GDFCustomDrawCap;
    const tailCap = view.getUint8(22) as GDFCustomDrawCap;

    const useHotpoint = !!view.getUint8(23);
    const hotpointType = view.getUint8(24);
    const ellipseType = view.getUint8(25) as GDFCustomDrawEllipseType;
    const range = view.getUint16(26, true);

    const point: GDFPoint = [view.getInt32(32, true) / 10_000, view.getInt32(28, true) / 10_000];
    const menus = parseString(view, 36, 16);
    const menuItem = parseString(view, 52, 16);
    const tips = [
        parseString(view, 68 + 16 * 0, 16),
        parseString(view, 68 + 16 * 1, 16),
        parseString(view, 68 + 16 * 2, 16),
        parseString(view, 68 + 16 * 3, 16),
        parseString(view, 68 + 16 * 4, 16)
    ];

    const points = parsePoints(view, 164, pointsCount, shape === GDFCustomDrawShape.polygon);
    const properties = parseString(view, 164 + pointsCount * 8, propertyLen);

    return {
        type: GDFRecordType.custom,
        shape: shape,
        usePen: usePen,
        penWidth: penWidth,
        penStyle: penStyle,
        penColor: penColor,
        useFill: useFil,
        fillStyle: fillStyle,
        fillColor: fillColor,
        headCap: headCap,
        tailCap: tailCap,
        useHotPoint: useHotpoint,
        ellipseType: ellipseType,
        range: range,
        menu: menus,
        menuItem: menuItem,
        tipText: tips,
        point: point,
        points: points,
        properties: properties
    };
}

function parseIataTurbRecord(view: DataView): GDFIataTurbRecord {
    const f1 = view.getUint32(16, true);
    const f2 = view.getUint32(20, true);
    const reportTime = valueOrNull(view.getUint32(12, true), 0);
    return {
        type: GDFRecordType.iataturb,
        point: [view.getInt32(8, true) / 100_000, view.getInt32(4, true) / 100_000],
        reportTime: reportTime ? reportTime + 631152000 : null,
        altitude: valueOrNull(f1 & 0x1ffff, 0x1ffff),
        meanEdr: valueOrNull((f1 >> 17) & 0x3ff, 0x3ff),
        meanConf: valueOrNull((f1 >> 27) & 0x0f, 0x0f),
        windSpeed: valueOrNull(f2 & 0xff, 0xff),
        windDir: valueOrNull((f2 >> 8) & 0x1ff, 0x1ff),
        peakEdr: valueOrNull((f2 >> 17) & 0x3ff, 0x3ff),
        peakConf: valueOrNull((f2 >> 27) & 0x0f, 0x0f),
        temp: valueOrNull(view.getInt8(24), 127)
    };
}

function parseLightingRecord(record: GDFLightningRecord, index: number, view: DataView) {
    record.points[index * 2 + 0] = mercatorUtils.mercatorXfromLng(view.getInt32(9, true) / 10_000) % 1; // Even though those are points for some reason they go past -180,180;
    record.points[index * 2 + 1] = mercatorUtils.mercatorYfromLat(view.getInt32(5, true) / 10_000);
    record.timeDev[index] = -1 * Math.abs(view.getInt16(3, true)); // All values should be negative but aren't :)
    record.amps[index] = view.getInt8(13);
    record.strokes[index] = view.getUint8(14);
}

function parseWmoStormRecord(view: DataView): GDFWmoStormRecord {
    return {
        type: GDFRecordType.wmostorm,
        point: [view.getInt32(7, true) / 10_000, view.getInt32(3, true) / 10_000],
        stormType: view.getUint8(11),
        name: '"' + parseString(view, 29, view.getUint8(12)) + '"'
    };
}

function parseWmoTropRecord(view: DataView): GDFWmoTropRecord {
    return {
        type: GDFRecordType.wmotrop,
        point: [view.getInt32(7, true) / 10_000, view.getInt32(3, true) / 10_000],
        subType: view.getUint8(11),
        height: valueOrNull(view.getUint16(12, true), 0xffff)
    };
}

function parseWmoJetStreamPoint(view: DataView): GDFWmoJetStreamPoint {
    return {
        point: [view.getInt32(4, true) / 10_000, view.getInt32(0, true) / 10_000],
        height: valueOrNull(view.getUint16(8, true), 0xffff),
        speed: valueOrNull(view.getUint8(10), 0xff),
        lowerDepth: valueOrNull(view.getUint16(11, true), 0xffff),
        upperDepth: valueOrNull(view.getUint16(13, true), 0xffff)
    };
}

function parseWmoRadiationRecord(view: DataView): GDFWmoRadationRecord {
    return {
        type: GDFRecordType.wmohzdrad,
        point: [view.getInt32(7, true) / 10_000, view.getInt32(3, true) / 10_000],
        time: parseDateTime(view, 11)
    };
}

function parseWmoJetStreamRecord(view: DataView): GDFWmoJetStreamRecord {
    const points: GDFWmoJetStreamPoint[] = [];
    const count = view.getUint16(3, true);
    const struct_size = view.getUint16(5, true);
    const offset = view.getUint16(7, true);
    for (let i = 0; i < count; i++) {
        points.push(parseWmoJetStreamPoint(new DataView(view.buffer, view.byteOffset + offset + i * struct_size, struct_size)));
    }
    return {
        type: GDFRecordType.wmojetstream,
        points: points
    };
}

function parseWmoCatRecord(view: DataView): GDFWmoCatRecord {
    return {
        type: GDFRecordType.wmocat,
        severity: view.getUint8(3),
        base: valueOrNull(view.getUint16(4, true), 0xffff),
        top: valueOrNull(view.getUint16(6, true), 0xffff),
        points: parsePoints(view, 26, view.getUint16(8, true), true)
    };
}

function parsePrivateTapsRecords(view: DataView): GDFTapsPrivateRecord {
    return {
        acid: parseString(view, 0, 7),
        tail: parseString(view, 7, 7)
    };
}

function parseTapsRecord(view: DataView): GDFTapsRecord {
    const flags1 = view.getUint32(24, true);
    const fixedLength = view.getUint8(3);
    const privateLength = view.getUint8(4);

    return {
        type: GDFRecordType.taps,
        point: [view.getInt32(9, true) / 10_000, view.getInt32(5, true) / 10_000],
        reportTime: parseDateTime(view, 13),
        acType: parseString(view, 20, 4),
        turbType: flags1 & 0x0f,
        altitude: valueOrNull((flags1 >> 4) & 0x3ff, 0x3ff),
        windSpeed: valueOrNull((flags1 >> 14) & 0xff, 0xff),
        windDir: valueOrNull((flags1 >> 22) & 0x1ff, 0x1ff),
        temp: valueOrNull(view.getInt8(28), -127),
        hazardMetric: valueOrNull(view.getInt16(29, true), 0xffff),
        hazardScaled: valueOrNull(view.getInt16(31, true), 0xffff),
        edr: valueOrNull(view.getInt16(33, true), 0xffff),
        rmsLoad: valueOrNull(view.getInt16(35, true), 0xffff),
        private: privateLength > 0 ? parsePrivateTapsRecords(new DataView(view.buffer, view.byteOffset + fixedLength, privateLength)) : null
    };
}

/*
struct GDF2_REC_WMOVOLCANO2
{
   GDF2_REC_HEADER sHdr;
 
   3 __int32 nUpperRightBoundLat;     // Lat coord of volcano UpperBound, 1/100,000 degree 
   7 __int32 nUpperRightBoundLon;     // Lon coordinate of volcano UpperBound, 1/10,0000 degree
   11 __int32 nLowerLeftBoundLat;     // Lat coord of volcano LowerBound, 1/100,000 degree
   15 __int32 nLowerLeftBoundLon;     // Lon coordinate of volcano LowerBound, 1/100,000 degree
   19 WORD wAshCloudTop;              // Cloud base in 100s of feet (max of all ash clouds) (0=surface, 0xFFFF=unknown/unused)
   21 WORD wAshCloudBase;             // Cloud tops in 100s of feet (min of all ash clouds) (0xFFFF=unknown/unused)
   23 __int32 nLatitude;              // Lat coord of volcano, 1/100,000 degree
   27 __int32 nLongitude;             // Lon coordinate of volcano, 1/100,000 degree
   31 char pcName[23];                // Volcano name, null terminated ....... (per ICAO 2007 annex 3, it seems this field can be 22 chars long so add 1 for null)
   54 char pcID[7];                   // IAVCEI numeric ID of volcano (per ICAO 2007 annex 3, this field can be in the form "nnnn-nn" so 7 chars provided. Append trailing blanks if needed. Set to nulls if not provided
   61 WORD wSummit;                   // Summit of volcano in meters (Australia BOM requirements wanted to see both ft and meters 0xFFFF if not known/provided
   63 char cAvColorCode;              // Aviation color code  0=unknown/notprovided  1=normal  2=elevated activity 3=increased activity/minor eruption/potential for major eruption 4=Warning eruption imminent or occurring   
   64 GDF2_TIME sReportTime;          // Date/time of report (UTC) set to nulls if not provided
   71 GDF2_TIME sNextReport;          // Next report date/time (UTC) if provided. If not set to nulls
   78 char pcAdvNum[9];               // Advisory number format is YYYY/NNNN  YYYY=year NNNN=number (place leading 0s) set to nulls of not provided
   87 char pcDetails[64];             // Eruption details ... (per ICAO 2007 annex 3 up to 64 characters are allowed) ... fill trailing characters with nulls if needed  set to nulls if not provided  
   151 BYTE cOperational : 1;          // 1=Operational, 0=Non-operational
       BYTE cNonOpType : 2;            // 0=NULL (for all operational), 1=Test, 2=Exercise (test and exercise only apply for non-operational)
       BYTE cReserved : 5;    
   152 WORD wPropertiesLen;           // Length of accompanied properties text at end of struct
   154 WORD wPropsByteOffset;         // Number of bytes from start of struct to start of properties text
   156 BYTE cAshCloudCount;           // Count of ash cloud structures that follow this record (0-255)

   157 char pcReserved[16];           // Reserved, set to nulls
 
   char pcPropertiesText[];       // Properties text if provided follows struct
   GDF2_REC_ASHCLOUD2 psCloud[];      // Array of ash cloud structures
   GDF_COORD_LG psPoints[];           // 1/100,000 degree lat/lon resolution
};

struct GDF2_REC_ASHCLOUD2
{
    0   BYTE cInterval;               // 0 = unknown, 1 = current, 2 = +6 hrs, 3 = +12hrs, 4 = +18hrs
    1   WORD wDirection;              // Direction of cloud movement 0-360 or 0xFFFF if not provided
    3   BYTE cSpeed;                  // Speed of cloud movement knots or 0xFF if unknown/notprovided
    4   WORD wBase;                   // Cloud base if surface use 0 in 100s of feet  0xFFFF=unknown/unused 
    6   WORD wTops;                   // Cloud tops in 100s of feet
    8   WORD wAshConcentration;       // Concentration in 100s mg/m3, 0xFFFF=Unknown/not provided
    10  WORD wPoints;                 // Number of points in ash/cloud polygon
    12  WORD wByteOffset;             // Offset of beginning of GDF_COORD_LG byte array relative to byte 1 of GDF2_REC_WMOVOLCANO
};
*/

function parseWmoVolcano2CloudRecord(cloudView: DataView, recordView: DataView): GDFWmoVolcano2CloudRecord {
    const pointsCount = cloudView.getUint16(10, true);
    const pointsOffset = cloudView.getUint16(12, true);

    return {
        type: GDFSubRecordType.wmoAshCloud2,
        interval: cloudView.getUint8(0),
        base: valueOrNull(cloudView.getUint16(4, true), 0xffff),
        tops: valueOrNull(cloudView.getUint16(6, true), 0xffff),
        concentration: valueOrNull(cloudView.getUint16(8, true), 0xffff),
        points: parsePoints(recordView, pointsOffset, pointsCount, true)
    };
}

function parseWmoVolcano2Record(view: DataView): GDFWmoVolcano2Record {
    const cloudCount = view.getUint8(156);
    const propertiesLength = view.getUint16(152, true);
    const propertiesOffset = view.getUint16(154, true);
    const cloudsOffset = propertiesOffset + propertiesLength;
    const clouds = [] as GDFWmoVolcano2CloudRecord[];
    const flag = view.getUint8(151);
    for (let i = 0; i < cloudCount; i++) {
        clouds.push(parseWmoVolcano2CloudRecord(new DataView(view.buffer, view.byteOffset + cloudsOffset + i * 14, 14), view));
    }

    return {
        type: GDFRecordType.wmovolcano2,
        point: [view.getInt32(27, true) / 10_000, view.getInt32(23, true) / 10_000],
        name: parseString(view, 31, 23),
        id: parseString(view, 54, 7),
        summit: valueOrNull(view.getUint16(61, true), 0xffff),
        color: view.getUint8(63),
        reportTime: parseDateTime(view, 64),
        nextReportTime: parseDateTime(view, 71),
        advisory: parseString(view, 78, 9),
        details: parseString(view, 87, 64),
        properties: parseString(view, propertiesOffset, propertiesLength),
        operational: !(flag & 0x01),
        noOpType: (flag >> 1) & 0x03,
        clouds
    };
}

function parseDateTime(view: DataView, offset = 0): number | null {
    const year = view.getInt16(0 + offset, true);
    if (year === 0) {
        return null;
    }
    const month = view.getUint8(2 + offset);
    const day = view.getUint8(3 + offset);
    const hour = view.getUint8(4 + offset);
    const minute = view.getUint8(5 + offset);
    const second = view.getUint8(6 + offset);
    return Date.UTC(year, month - 1, day, hour, minute, second, 0) / 1000;
}

function valueOrNull(value: number, nullValue: number, multiplier?: number | undefined): number | null {
    return value === nullValue ? null : value * (multiplier ?? 1.0);
}

function valueCharOrNull(value: number, nullValue: number): string | null {
    return value === nullValue ? null : String.fromCharCode(value);
}

function parseGEO_COORD_INT(view: DataView): GDFPoint {
    const wLongitude = view.getUint16(0, true);
    const wLatitude = view.getUint16(2, true);

    const tmp4 = view.getUint8(4);
    const cLonUpper4 = tmp4 & 0xf;
    const cLatUpper4 = (tmp4 >>> 4) & 0xf;

    return [(cLonUpper4 * 65_536 + wLongitude - 180_000) / 1_000, (cLatUpper4 * 65_536 + wLatitude - 180_000) / 1_000];
}

function parseSystemTime(originalView: DataView, offset: number = 0): number {
    const view = new DataView(originalView.buffer, originalView.byteOffset + offset);
    const wYear = view.getUint16(0, true);
    const wMonth = view.getUint16(2, true);
    //const wDayOfWeek = view.getUint16(4, true);
    const wDay = view.getUint16(6, true);
    const wHour = view.getUint16(8, true);
    const wMinute = view.getUint16(10, true);
    const wSecond = view.getUint16(12, true);
    const wMilliseconds = view.getUint16(14, true);
    return Date.UTC(wYear, wMonth - 1, wDay, wHour, wMinute, wSecond, wMilliseconds) / 1000;
}

function parsePoints(view: DataView, offset: number, count: number, closePolygon?: boolean): GDFPoint[] {
    if (count === 0) {
        return [];
    }

    const points: GDFPoint[] = [];

    for (let i = 0; i < count; i++) {
        points.push([view.getInt32(offset + i * 8, true) / 10_000, view.getInt32(offset + 4 + i * 8, true) / 10_000]);
    }

    if (closePolygon) {
        points.push([...points[0]]);
    }

    return points;
}

function parseString(view: DataView, offset: number, length: number): string {
    return decoder
        .decode(view.buffer.slice(view.byteOffset + offset, view.byteOffset + offset + length) as ArrayBuffer)
        .replaceAll('\0', '')
        .replaceAll('\r', '\n') // There are sometimes \r sometimes \n and sometimes \r\n
        .replaceAll('\n\n', '\n')
        .trim();
}

function colorFromNumber(input: number): string {
    const b = input & 0xff;
    const g = (input >> 8) & 0xff;
    const r = (input >> 16) & 0xff;
    const a = ((input >> 24) & 0xff) / 255;

    return `rgba(${r},${g},${b},${a.toFixed(2)})`;
}
